import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import RHFInput from './RHF/RHFInput';
import { Turnstile } from '@marsidev/react-turnstile';
import { useEffect, useState } from 'react';
import { RHFSubmitButton } from './RHF/RHFSubmitButton';
import type { RegisterViewModel } from '../models/AccountViewModels/RegisterViewModel';
import AuthApi from '../apis/AuthApi';
import { setToken } from '../apis/LocalStorageApi';
import { handleError } from '../apis';
import { useUserDetails } from '../helpers/UserContext';
import { toast } from 'react-toastify';

const registerSchema = z
  .object({
    firstName: z.string('Required!').trim().min(1, 'Required!'),
    lastName: z.string('Required!').trim().min(1, 'Required!'),
    email: z.email('Required!').trim(),
    password: z
      .string('Required!')
      .trim()
      .min(6, { message: 'Password must be at least 6 characters long' })
      .refine(
        (value) => /[a-z]/.test(value),
        'Password must contain a lowercase character',
      )
      .refine(
        (value) => /[A-Z]/.test(value),
        'Password must contain a uppercase character',
      )
      .refine(
        (value) => /[!@#$%^&*]/.test(value),
        'Password must contain a special character (!@#$%^&*)',
      )
      .refine((value) => /\d/.test(value), 'Password must contain a number'),
    confirmPassword: z.string('Required!').trim(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'], // path of error
  });

export type RegisterSchema = z.infer<typeof registerSchema>;

export default function RegisterForm() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [, , setUserFromToken, hasAuth] = useUserDetails();
  const isAuthorized = hasAuth();

  useEffect(() => {
    if (isAuthorized) {
      navigate('/alpacas');
    }
  }, [isAuthorized, navigate]);

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<RegisterSchema>({
    resolver: zodResolver(registerSchema),
  });

  const handleRegister = async (values: RegisterSchema) => {
    setErrorMessage('');
    if (status === '') {
      toast.error('Please complete the CAPTCHA');
      return;
    }
    const submitValues = {
      ...values,
      cfTurnstileResponse: status,
    } as RegisterViewModel;

    try {
      const response = await AuthApi.register(submitValues);
      if (response.token) {
        setToken(response.token as string);
        setUserFromToken(response.token as string);
        // navigate('/alpacas');
      } else {
        const errorResponse: string[] = [];
        for (const prop in response) {
          const value = (response as unknown as { [key: string]: string[] })[
            prop
          ];
          if (Array.isArray(value)) {
            errorResponse.push(value[0]);
          } else {
            errorResponse.push(value);
          }
        }
        const message = errorResponse.join(', ');
        setErrorMessage(message);
      }
    } catch (error) {
      const message = await handleError(error);
      setErrorMessage(message);
    }
  };

  return (
    <div
      className='flex w-[320px] flex-initial flex-col items-center justify-start self-auto rounded-2xl border border-gray-500 p-3 dark:border-gray-500 '
      id='login-form'
    >
      <p className='mb-2'>
        Register or{' '}
        <Link className='font-bold' to='/'>
          Login
        </Link>
      </p>
      <form onSubmit={handleSubmit(handleRegister)}>
        <RHFInput
          name='firstName'
          label='First Name'
          type='text'
          register={register('firstName')}
          errors={errors}
        />
        <RHFInput
          name='lastName'
          label='Last Name'
          type='text'
          register={register('lastName')}
          errors={errors}
        />
        <RHFInput
          name='email'
          label='Email'
          type='text'
          register={register('email')}
          errors={errors}
        />
        <RHFInput
          name='password'
          label='Password'
          type='password'
          register={register('password')}
          errors={errors}
        />
        <RHFInput
          name='confirmPassword'
          label='Confirm Password'
          type='password'
          register={register('confirmPassword')}
          errors={errors}
        />
        <div id='cf-turnstile' className='flex items-center justify-center'>
          <Turnstile
            siteKey='0x4AAAAAAA3HFdGjg5hs9TLl'
            onError={() => setStatus('')}
            onExpire={() => setStatus('')}
            onSuccess={(token) => setStatus(token)}
          />
        </div>
        {errorMessage && (
          <span className='text-xs text-rose-600 dark:text-rose-400'>
            {errorMessage}
          </span>
        )}
        {status !== '' && <RHFSubmitButton isSubmitting={isSubmitting} />}
      </form>
    </div>
  );
}
